// components/FinalSicReport.jsx
import React, { useState, useEffect, useMemo } from 'react';
import Api from '../../api/global.service';
import { useAuth } from '../../components/content/auth.jsx';
import xlsx from 'json-as-xlsx';

// Styling/components from Sirio
import CardContainer from '../../components/card/card-container.jsx';
import NoPagination from '../../components/pagination/noPagination.jsx';
import env from '../../environment.js';

const FinalSicReport = () => {
  const auth = useAuth();
  const [selectedCooperativa, setSelectedCooperativa] = useState('');
  const [selectedCiclo, setSelectedCiclo] = useState('');
  const [cooperativas, setCooperativas] = useState([]);
  const [ciclos, setCiclos] = useState([]);
  const [reportData, setReportData] = useState([]);
  const [blocking, setBlocking] = useState(false);
  const [error, setError] = useState(null);

  // Helper function to build dynamic URLs
  const buildDynamicUrl = url => {
    if (!url || url === 'No URL') return null;

    // If it's already a full URL (starts with http/https), return as is
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }

    // If it's a relative path, construct the full URL using current origin
    if (url.startsWith('/')) {
      return `${window.location.origin}${url}`;
    }

    // If it's just a path without leading slash, add it
    return `${window.location.origin}/${url}`;
  };

  // Load cooperativas
  useEffect(() => {
    loadCooperativas();
  }, []);

  // Load ciclos when cooperativa changes
  useEffect(() => {
    if (selectedCooperativa) {
      loadCiclos(selectedCooperativa);
    } else {
      setCiclos([]);
      setSelectedCiclo('');
    }
  }, [selectedCooperativa]);

  // Remove automatic report loading - now controlled by button click
  // useEffect(() => {
  //   if (selectedCooperativa && selectedCiclo) {
  //     loadReport();
  //   }
  // }, [selectedCooperativa, selectedCiclo]);

  const loadCooperativas = async () => {
    try {
      const response = await Api.getAll('cooperativas');
      const list = response.data || [];
      setCooperativas(list);
      // Preselect user's cooperativa if available
      if (auth?.user?.id_cooperativa && list.some(c => c.id === auth.user.id_cooperativa)) {
        setSelectedCooperativa(String(auth.user.id_cooperativa));
      }
    } catch (err) {
      console.error('Error loading cooperativas:', err);
    }
  };

  const loadCiclos = async coopId => {
    try {
      const response = await Api.search('ciclos', {
        filter: [{ column: 'id_cooperativa', operator: '=', value: Number(coopId) }],
      });
      setCiclos(response.data || []);
      setSelectedCiclo(''); // Reset selected ciclo when cooperativa changes
      // Clear report data when cooperativa changes
      setReportData([]);
      setError(null);
    } catch (err) {
      console.error('Error loading ciclos:', err);
      setCiclos([]);
      setSelectedCiclo('');
      setReportData([]);
      setError(null);
    }
  };

  const loadReport = async () => {
    if (!selectedCooperativa || !selectedCiclo) return;

    setBlocking(true);
    setError(null);
    setReportData([]);

    try {
      const response = await Api.getAll('reports/sic-final', {
        id_cooperativa: Number(selectedCooperativa),
        id_ciclo: Number(selectedCiclo),
      });
      setReportData(response.data?.data || []);
    } catch (err) {
      const serverMsg = err?.response?.data?.error || err.message;
      setError('Error de conexión: ' + serverMsg);
    } finally {
      setBlocking(false);
    }
  };

  // Function to handle ciclo change and clear data
  const handleCicloChange = e => {
    setSelectedCiclo(e.target.value);
    // Clear report data when ciclo changes
    setReportData([]);
    setError(null);
  };

  // Excel download function
  const exportarExcel = () => {
    if (reportData.length === 0) return;

    // Get all available columns from the first data row
    const allColumns = Object.keys(reportData[0]);

    const xlsData = [
      {
        sheet: 'Reporte SIC Final',
        columns: allColumns.map(column => ({
          label: column,
          value: column,
        })),
        content: reportData,
      },
    ];

    const settings = {
      fileName: `reporte_sic_final_${selectedCooperativa}_${selectedCiclo}`,
    };

    xlsx(xlsData, settings);
  };

  // Table columns in NoPagination style
  const columns = useMemo(
    () => [
      {
        Header: 'REPORTE FINAL SIC',
        columns: [
          {
            Header: '--',
            columns: [
              {
                Header: 'ID FICHA SIC',
                accessor: 'id_sic_display',
                minWidth: 80,
                sortable: true,
                Cell: ({ row }) => row.original['ID Ficha SIC'],
              },
              {
                Header: 'TIPO DE ERROR',
                accessor: 'tipo_error_display',
                minWidth: 150,
                sortable: true,
                Cell: ({ row }) => {
                  const errorTypes = row.original['Tipo de error'];
                  if (!errorTypes) return null;

                  // Split by comma and trim whitespace
                  const errorList = errorTypes.split(',').map(error => error.trim());

                  return (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                      {errorList.map((error, index) => (
                        <span key={index} className="badge bg-warning" style={{ margin: '1px' }}>
                          {error}
                        </span>
                      ))}
                    </div>
                  );
                },
              },
              {
                Header: 'NOMBRE DEL PRODUCTOR',
                accessor: 'nombre_productor_display',
                minWidth: 220,
                sortable: true,
                Cell: ({ row }) => {
                  const url = row.original['Link a Página del Productor'];
                  const name = row.original['Nombre del productor'];
                  if (url && url !== 'No URL') {
                    return (
                      <a href={buildDynamicUrl(url)} rel="noopener noreferrer" style={{ color: '#789D16' }}>
                        {name}
                      </a>
                    );
                  }
                  return name;
                },
              },
              {
                Header: 'NOMBRE DE LA PARCELA',
                accessor: 'nombre_parcela_display',
                minWidth: 180,
                sortable: true,
                Cell: ({ row }) => row.original['Nombre de la parcela'],
              },
            ],
          },
        ],
      },
    ],
    []
  );

  // Header colors/format
  const getBackgroundColor = (row, column) => {
    if (row === 0) {
      if (column % 2 === 0) return '#5F5F59';
      return '#91918A';
    }
    if (row === 1) {
      if (column > 2 && column % 2 === 0) return 'rgb(247, 239, 231)';
      return 'rgb(247, 239, 231)';
    }
    return 'rgb(247, 239, 231)';
  };
  const getColor = row => (row === 0 ? 'white' : 'rgb(95, 95, 89)');

  return (
    <div>
      {/* header controls and CSV download button (top bar) */}
      <div className="row justify-content-between mb-2">
        {/* Left: filters */}
        <div className="col-6">
          <div className="row">
            <div className="col-md-6 mb-2">
              <div style={{ height: '43px' }}>
                <select
                  id="cooperativa"
                  className="form-control select-ciclo"
                  value={selectedCooperativa}
                  onChange={e => {
                    setSelectedCooperativa(e.target.value);
                    setReportData([]); // clear results when changing filters
                  }}>
                  <option value="">Seleccionar cooperativa</option>
                  {cooperativas.map(coop => (
                    <option key={coop.id} value={coop.id}>
                      {coop.nombre}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="col-md-6 mb-2">
              <div style={{ height: '43px' }}>
                <select id="ciclo" className="form-control select-ciclo" value={selectedCiclo} onChange={handleCicloChange}>
                  <option value="">Seleccionar ciclo</option>
                  {ciclos.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.nombre}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Center: Generate Report button */}
        <div className="col-3 align-self-center">
          <button
            onClick={loadReport}
            disabled={!selectedCooperativa || !selectedCiclo || blocking}
            className="btn btn-primary"
            type="button"
            title="Generar Reporte"
            style={{
              backgroundColor: '#789D16',
              borderColor: '#789D16',
              color: 'white',
              fontSize: '0.9rem',
              padding: '8px 16px',
            }}>
            {blocking ? (
              <span>
                <i className="fas fa-spinner fa-spin mr-2"></i>
                Cargando...
              </span>
            ) : (
              'Generar Reporte'
            )}
          </button>
        </div>

        {/* Right: top-right Excel download (only when there is data) */}
        <div className="col-3 align-self-center">
          {reportData.length > 0 && (
            <div className="d-flex justify-content-end">
              <button onClick={exportarExcel} className="btn m-r-2" type="button" title="Descargar Excel">
                <img src={env.images.icon.xls} className="pr-1" />
                <span style={{ color: '#789D16', fontSize: '0.76rem' }}>Descargar Excel</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Table list in CardContainer + NoPagination for consistent style */}
      <CardContainer height={'100%'}>
        {/* Error */}
        {error && <div className="alert alert-danger mb-2">{error}</div>}

        {/* Empty state helper text (when filters selected but no data) */}
        {!blocking && !error && reportData.length === 0 && selectedCooperativa && selectedCiclo && (
          <div className="text-center text-muted mb-2">
            Selecciona cooperativa y ciclo, luego haz clic en "Generar Reporte" para cargar los datos.
          </div>
        )}

        {/* Empty state when no filters selected */}
        {!blocking && !error && reportData.length === 0 && (!selectedCooperativa || !selectedCiclo) && (
          <div className="text-center text-muted mb-2">Selecciona una cooperativa y un ciclo para generar el reporte.</div>
        )}

        <NoPagination blocking={blocking} columns={columns} data={reportData} fnBackgroundColor={getBackgroundColor} fnColor={getColor} />
      </CardContainer>
    </div>
  );
};

export default FinalSicReport;
